import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { canAccess } from '../../utils/rbac';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { formatDate } from '../../utils/format';
import { STATUS_LABELS, STATUS_TONES, OPEN_STATUSES, kindOf, KINDS, KIND_LABELS } from '../../utils/siteSurveys';
import { SURVEYS_TOUR_ID, surveysTourSteps } from './surveysTour';
import SurveyForm from './SurveyForm';

// Surveys: every site survey across the opportunities and bids
// (src/api/routes/surveys.js). A survey is requested from its opportunity or
// bid; this page is where the engineer finds theirs and the PM finds what
// waits for review. Routed as page 'surveys'; a detail id opens one survey.

const FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'all', label: 'All' },
];

// Blank copies of the forms Ops issues, for a survey not raised in Ops yet.
// A survey that is in Ops downloads its own form, stamped with its reference.
const BLANK_NAMES = { room: 'Blank - Site Survey.docx', desks: 'Blank - Desk Survey.xlsx', moves: 'Blank - Desk Move.xlsx' };

function BlankForms() {
  const [error, setError] = useState('');
  const get = async (kind) => {
    try {
      const blob = await api.getBlob(`surveys/forms/${kind}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = BLANK_NAMES[kind];
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setError('');
    } catch (e) { setError(e.message); }
  };
  return (
    <div data-tour="blank-forms" className="flex flex-col items-end gap-0.5">
      <div className="flex flex-wrap items-center justify-end gap-1">
        <span className="text-[9px] text-gray-500">Blank forms:</span>
        {KINDS.map(k => (
          <button key={k} onClick={() => get(k)} className="px-2 py-0.5 rounded border border-gray-200 bg-white text-[10px] text-gray-700 hover:border-navy hover:text-navy"
            title={`A blank ${KIND_LABELS[k].toLowerCase()} form. For a survey already in Ops, download its own form from the survey instead.`}>
            {KIND_LABELS[k]}
          </button>
        ))}
      </div>
      {error && <div role="alert" className="text-[10px] text-red-600">{error}</div>}
    </div>
  );
}

export default function SurveysPage({ initialSurveyId }) {
  const { navigate, currentUser } = useApp();
  const [data, setData] = useState({ surveys: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState('open');
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    api.get('surveys')
      .then(d => { setData(d); setLoadError(''); })
      .catch(e => setLoadError(e?.message || 'Could not load the surveys'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const tour = useFeatureTour(SURVEYS_TOUR_ID, { enabled: !loading && !loadError && !initialSurveyId });

  if (initialSurveyId) {
    return <SurveyForm key={initialSurveyId} surveyId={initialSurveyId} onBack={() => navigate('surveys')} onChanged={load} />;
  }

  const q = search.trim().toLowerCase();
  const shown = data.surveys.filter(s =>
    (filter === 'all' || (filter === 'open' ? OPEN_STATUSES.includes(s.status) : s.status === filter)) &&
    (!mine || s.assigned_to === currentUser?.id) &&
    (!q || [s.reference, s.title, s.location, s.opportunity_reference, s.opportunity_detail, s.bid_reference, s.bid_opportunity, s.client_name, s.client_reference]
      .some(v => String(v || '').toLowerCase().includes(q))));
  const countOf = (f) => data.surveys.filter(s => (f === 'all' || (f === 'open' ? OPEN_STATUSES.includes(s.status) : s.status === f))).length;

  if (loading) return <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>;
  if (loadError) return <div className="text-center py-8 text-[10px] text-red-600">{loadError}</div>;

  return (
    <div className="fade-in">
      <div data-tour="page-header" className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Surveys</h2>
          <div className="text-[9px] text-gray-400 mt-0.5">Site surveys for physical work. Request one from its opportunity or bid.</div>
        </div>
        <div className="flex items-start gap-2">
          <BlankForms />
          <button onClick={tour.start} className="w-[23px] h-[23px] rounded-full border border-gray-200 text-gray-500 hover:text-navy hover:border-navy text-[11px] font-bold shrink-0" aria-label="Replay the guided tour of this page" title="Replay the guided tour of this page">?</button>
        </div>
      </div>

      <div data-tour="filters" className="flex flex-wrap items-center gap-2 mb-3">
        {FILTERS.map(f => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 text-[9px] font-semibold rounded-full border ${filter === f.value ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}>
            {f.label} ({countOf(f.value)})
          </button>
        ))}
        <label className="flex items-center gap-1 text-[9px] text-gray-600" title="Only the surveys where you are the engineer"><input type="checkbox" checked={mine} onChange={e => setMine(e.target.checked)} />Mine</label>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search surveys" aria-label="Search surveys"
          className="ml-auto border border-gray-200 rounded px-2 py-1 text-[10px] bg-white w-48" />
      </div>

      <div data-tour="table" className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead className="bg-gray-50 text-[8px] uppercase text-gray-400">
            <tr>
              <th className="text-left px-2 py-1.5">Ref</th><th className="text-left px-2 py-1.5">Room or area</th>
              <th className="text-left px-2 py-1.5">Opportunity or bid</th><th className="text-left px-2 py-1.5">Client</th>
              <th className="text-left px-2 py-1.5">Engineer</th><th className="text-left px-2 py-1.5">Visit</th>
              <th className="text-left px-2 py-1.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(s => (
              <tr key={s.id} onClick={() => navigate('surveys', s.id)} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                <td className="px-2 py-1.5 font-mono text-gold font-bold">{s.reference}</td>
                <td className="px-2 py-1.5 text-navy">
                  {s.title}
                  {kindOf(s) !== 'room' && <span className="ml-1 px-1 rounded text-[8px] font-bold bg-blue-50 text-blue-700">{kindOf(s) === 'moves' ? 'Desk move' : 'Desks'}</span>}
                  {!!s.paper_only && <span className="ml-1 px-1 rounded text-[8px] font-bold bg-amber-50 text-amber-700">Paper</span>}
                </td>
                <td className="px-2 py-1.5 text-gray-600">
                  {s.opportunity_reference
                    ? <><span className="font-mono">{s.opportunity_reference}</span> {s.opportunity_detail}</>
                    : s.bid_reference ? <>Bid <span className="font-mono">{s.bid_reference}</span> {s.bid_opportunity}</> : '-'}
                </td>
                <td className="px-2 py-1.5 text-gray-600">{[s.client_name, s.client_reference].filter(Boolean).join(' · ') || '-'}</td>
                <td className="px-2 py-1.5 text-gray-600">{s.assigned_name || '-'}</td>
                <td className="px-2 py-1.5 text-gray-600">{s.visit_date ? formatDate(s.visit_date) : '-'}</td>
                <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${STATUS_TONES[s.status]}`}>{STATUS_LABELS[s.status]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && (
          <div className="text-center py-6 text-[10px] text-gray-400">
            {data.surveys.length === 0 ? 'No surveys yet. Request one from the Surveys tab of an opportunity or a bid.' : 'Nothing matches these filters.'}
          </div>
        )}
      </div>

      {tour.open && <FeatureTour steps={surveysTourSteps} context={{ canReview: !!data.can_review, canRequest: canAccess('tracker') || canAccess('bid-tracker') }} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}
